import React, { useState } from "react";
import { Formik, Form, Field, FieldArray, useFormikContext } from "formik";
import { Sheet, Button, Spinner, InputField, TextareaField } from "@ui";
import {
  useTool,
  useUpdateTool,
  type ToolUpdateInput,
  type ToolParameter,
} from "@agentic_system/api/tool";
import { ChevronDown, ChevronRight } from "lucide-react";

interface ToolEditDrawerProps {
  toolId: number | null;
  onClose: () => void;
}

interface FormValues {
  display_name: string;
  description: string;
  agent_prompt_support: string;
  parameters: Array<{
    id: number;
    name: string;
    type: string;
    required: boolean;
    default_description: string | null;
    custom_description: string;
  }>;
}

function FormFooter({ onClose }: { onClose: () => void }) {
  const { isSubmitting } = useFormikContext();
  return (
    <div className="flex justify-end gap-3">
      <Button variant="white" onClick={onClose} type="button">
        Annuler
      </Button>
      <Button type="submit" form="tool-edit-form" loading={isSubmitting}>
        Enregistrer
      </Button>
    </div>
  );
}

const EMPTY_VALUES: FormValues = {
  display_name: "",
  description: "",
  agent_prompt_support: "",
  parameters: [],
};

export function ToolEditDrawer({ toolId, onClose }: ToolEditDrawerProps) {
  const { data: tool, isLoading } = useTool(toolId ?? 0);
  const updateTool = useUpdateTool();
  const [expandedParams, setExpandedParams] = useState<Set<string>>(new Set());

  const handleSubmit = async (values: FormValues) => {
    if (!toolId) return;
    const payload: ToolUpdateInput = {
      display_name: values.display_name || undefined,
      description: values.description || undefined,
      agent_prompt_support: values.agent_prompt_support || undefined,
      parameters_attributes: values.parameters.map((p) => ({
        id: p.id,
        description: p.custom_description || undefined,
      })),
    };
    await updateTool.mutateAsync({ id: toolId, input: payload });
    onClose();
  };

  const toggleParam = (name: string) => {
    setExpandedParams((prev) => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
      }
      return next;
    });
  };

  const buildInitialValues = (tool: {
    display_name: string;
    description: string | null;
    agent_prompt_support: string | null;
    parameters?: ToolParameter[];
  }): FormValues => ({
    display_name: tool.display_name || "",
    description: tool.description || "",
    agent_prompt_support: tool.agent_prompt_support || "",
    parameters: (tool.parameters || []).map((p) => ({
      id: p.id,
      name: p.name,
      type: p.type,
      required: p.required,
      default_description: p.default_description,
      custom_description: p.custom_description || "",
    })),
  });

  const isOpen = toolId !== null;
  const initialValues = tool ? buildInitialValues(tool) : EMPTY_VALUES;

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ values }) => (
        <Sheet
          open={isOpen}
          onClose={onClose}
          title={tool?.label ?? "Edit Tool"}
          subtitle={tool?.identifier}
          width="lg"
          footer={
            isLoading || !tool ? undefined : <FormFooter onClose={onClose} />
          }
        >
          {isLoading || !tool ? (
            <div className="flex items-center justify-center py-12">
              <Spinner />
            </div>
          ) : (
            <Form id="tool-edit-form" className="space-y-6">
              {/* Display Name */}
              <Field name="display_name">
                {({ field }: { field: any }) => (
                  <InputField
                    {...field}
                    label="Nom d'affichage"
                    helperText="Nom lisible affiché dans le chat lors de l'exécution de l'outil. Laissez vide pour utiliser le nom technique."
                    placeholder={tool?.label || "ex: Recherche de franchises"}
                  />
                )}
              </Field>

              {/* Tool Description */}
              <Field name="description">
                {({ field }: { field: any }) => (
                  <TextareaField
                    {...field}
                    label="Description"
                    helperText="Décrivez ce que fait cet outil. Cela sera affiché à l'IA lors de la sélection des outils."
                    rows={4}
                    placeholder="Entrez une description pour cet outil..."
                  />
                )}
              </Field>

              {/* Agent Prompt Support */}
              <Field name="agent_prompt_support">
                {({ field }: { field: any }) => (
                  <TextareaField
                    {...field}
                    label="Support de prompt agent"
                    helperText="Instructions supplémentaires pour l'IA sur quand et comment utiliser cet outil."
                    rows={3}
                    placeholder="ex: Utilisez cet outil lorsque l'utilisateur demande..."
                  />
                )}
              </Field>

              {/* Parameters (editable descriptions) */}
              {values.parameters && values.parameters.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-slate-700 mb-3">
                    Paramètres ({values.parameters.length})
                  </h4>
                  <div className="border border-slate-200 rounded-lg divide-y divide-slate-200">
                    <FieldArray name="parameters">
                      {() =>
                        values.parameters.map((param, index) => {
                          const isExpanded = expandedParams.has(param.name);

                          return (
                            <div
                              key={param.id}
                              className="bg-white first:rounded-t-lg last:rounded-b-lg"
                            >
                              <button
                                type="button"
                                onClick={() => toggleParam(param.name)}
                                className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-slate-50 transition-colors"
                              >
                                <div className="flex items-center gap-3">
                                  {isExpanded ? (
                                    <ChevronDown className="w-4 h-4 text-slate-400" />
                                  ) : (
                                    <ChevronRight className="w-4 h-4 text-slate-400" />
                                  )}
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium text-slate-900">
                                      {param.name}
                                    </span>
                                    <span className="text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                                      {param.type}
                                    </span>
                                    {param.required && (
                                      <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
                                        requis
                                      </span>
                                    )}
                                    {param.custom_description && (
                                      <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                                        personnalisé
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </button>

                              {isExpanded && (
                                <div className="px-4 pb-4 pt-0 pl-11 space-y-3">
                                  {/* Default description from code */}
                                  {param.default_description && (
                                    <div>
                                      <label className="block text-xs font-medium text-slate-500 mb-1">
                                        Par défaut (depuis le code)
                                      </label>
                                      <p className="text-sm text-slate-600 bg-slate-50 rounded px-3 py-2">
                                        {param.default_description}
                                      </p>
                                    </div>
                                  )}

                                  {/* Editable custom description */}
                                  <Field
                                    name={`parameters.${index}.custom_description`}
                                  >
                                    {({ field }: { field: any }) => (
                                      <TextareaField
                                        {...field}
                                        label="Description personnalisée"
                                        helperText="Remplacez la description par défaut par la votre. Laissez vide pour utiliser celle par défaut."
                                        rows={2}
                                        placeholder="Entrez une description personnalisée..."
                                      />
                                    )}
                                  </Field>
                                </div>
                              )}
                            </div>
                          );
                        })
                      }
                    </FieldArray>
                  </div>
                  <p className="mt-2 text-xs text-slate-500">
                    Les types et contraintes des paramètres sont synchronisés
                    depuis le code. Les descriptions peuvent être
                    personnalisées.
                  </p>
                </div>
              )}
            </Form>
          )}
        </Sheet>
      )}
    </Formik>
  );
}
